import React, { Fragment, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { isBlank } from "../../../util/ext";
import {
  addIndex,
  concat,
  equals,
  identity,
  isEmpty,
  map,
  sortBy,
  toUpper,
  uniqBy,
  prop,
} from "ramda";
import { useContentUpdate } from "../../content/contentServices";
import { useRemoveContentFromCollection } from "../../collections/collectionServices";
import { useDeleteModuleItems } from "../../modules/moduleServices";
import clsx from "clsx";
import { useSnackAlert } from "../../../common/use-snackAlert";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import MediaDialog from "../../common/MediaDialog";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";

const useStyles = makeStyles(theme => ({
  actions: {
    padding: theme.spacing(5),
  },

  badge: {
    background: "rgba(125, 218, 252, 0.5)",
    borderRadius: 4,
    padding: "8px 12px",
    gap: 10,
    width: "auto",
    height: 32,
  },

  button: {
    width: "auto",
    textTransform: "capitalize",
  },

  mediaType: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: "16px",
    color: theme.palette.primary.main,
  },

  otherReason: {
    lineHeight: "2.5rem",
    maxHeight: 150,
    overflowY: "auto !important",
  },

  rejected: {
    backgroundColor: theme.palette.rejected,
    "&:hover": {
      backgroundColor: "rgba(176, 34, 12, 0.7)",
    },
  },

  title: {
    color: theme.palette.primary.darker,
    fontSize: "2.4rem",
    lineHeight: "3.2rem",
  },
}));

const reasons = [
  "Author Request",
  "Duplicate",
  "File / metadata mismatch",
  "Inappropriate / harmful subject matter",
  "Not a book",
  "Only submitted a cover",
  "Proof copy / Advanced Reader Copy",
  "Formatting issues / Missing Pages / Pages out of order",
  "Poorly scanned / illegible content",
  "Lack of necessary information",
  "Watermarked",
  "Other (provide reason)",
];

const mapIndexed = addIndex(map);

// content is an array of content items
const RejectedDialog = props => {
  const { open, content, handleClose, handleReject } = props;
  const classes = useStyles();
  const { SnackAlert, showAlert } = useSnackAlert();

  const allParents = uniqBy(
    prop("id"),
    content.flatMap(item =>
      concat(item.parentModules ?? [], item.parentCollections ?? []),
    ),
  );

  const [hasParents, setHasParents] = useState(!isEmpty(allParents));

  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [error, setError] = useState(false);
  const handleChange = event => {
    const reason = event.target.value;
    setReason(reason);
    setError(false);
  };

  const resetReason = () => {
    setReason("");
    setOtherReason("");
  };

  const handleCloseClick = () => {
    handleClose();
    resetReason();
    setError(false);
  };

  const updateContent = useContentUpdate();
  const submit = async () => {
    const error =
      reason === "Other (provide reason)"
        ? isBlank(otherReason)
        : isBlank(reason);
    setError(error);
    if (!error) {
      try {
        const rejectionNote =
          reason === "Other (provide reason)" ? otherReason : reason;

        await Promise.all(
          content.map(item =>
            updateContent.mutateAsync({
              resourceIds: { id: item.id },
              data: {
                stipProperties: [{ rejectionNote }],
                status: "REJECTED",
              },
            }),
          ),
        );

        handleCloseClick();
        handleReject();
      } catch (err) {
        console.log(err);
      }
    }
  };

  const removeContentFromCollection = useRemoveContentFromCollection();
  const deleteModuleItems = useDeleteModuleItems();
  const removeParents = async () => {
    try {
      await Promise.all(
        content.flatMap(item => {
          const parents = concat(
            item.parentModules ?? [],
            item.parentCollections ?? [],
          );
          return map(async parent => {
            try {
              if (equals(parent.mediaType, "module")) {
                await deleteModuleItems.mutateAsync({
                  id: parent.id,
                  ids: [item.id],
                });
              }

              if (equals(parent.mediaType, "collection")) {
                await removeContentFromCollection.mutateAsync({
                  collectionId: parent.id,
                  contentIds: [item.id],
                });
              }
            } catch (err) {
              return err.message;
            }
          }, parents);
        }),
      );

      setHasParents(false);
    } catch (err) {
      showAlert("error", [err.message]);
    }
  };

  useEffect(() => {
    setHasParents(
      !isEmpty(
        uniqBy(
          prop("id"),
          content.flatMap(item =>
            concat(item.parentModules ?? [], item.parentCollections ?? []),
          ),
        ),
      ),
    );
  }, [content]);

  return (
    <Fragment>
      <SnackAlert />

      <MediaDialog
        open={open}
        titleClassName={classes.title}
        title={hasParents ? "Wait!" : "Reason for Rejection"}
        handleClose={handleCloseClick}
        content={
          updateContent.isPending ||
          removeContentFromCollection.isPending ||
          deleteModuleItems.isPending ? (
            <PanelLoadingIndicator />
          ) : (
            <Grid container direction="column" spacing={4}>
              <Grid item xs={12}>
                <Typography>
                  {hasParents
                    ? "Are you sure you want to reject? Rejecting these items will also remove them from the following content groups:"
                    : "In order to reject these items and hide them from your list of approved content, please provide a reason for rejection."}
                </Typography>
              </Grid>

              {hasParents ? (
                <List>
                  {map(
                    parent => (
                      <ListItem key={parent.id}>
                        <ListItemText>{parent.name}</ListItemText>
                        <div className={classes.badge}>
                          <Typography className={classes.mediaType}>
                            {toUpper(parent.mediaType)}
                          </Typography>
                        </div>
                      </ListItem>
                    ),
                    allParents,
                  )}
                </List>
              ) : (
                <Fragment>
                  <Grid item xs={12}>
                    <FormControl fullWidth error={error}>
                      <InputLabel>Select a reason for rejection</InputLabel>
                      <Select
                        labelId="rejection-reason-label"
                        id="rejection-reason"
                        value={reason}
                        onChange={handleChange}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                        }}
                      >
                        {mapIndexed(
                          (reason, index) => (
                            <MenuItem key={`reason-${index}`} value={reason}>
                              {reason}
                            </MenuItem>
                          ),
                          sortBy(identity, reasons),
                        )}
                      </Select>
                      {error && (
                        <FormHelperText>Please provide a reason</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {equals(reason, "Other (provide reason)") && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        label="Other Reason"
                        value={otherReason}
                        onChange={event => setOtherReason(event.target.value)}
                        inputProps={{
                          maxLength: 4096,
                        }}
                        InputProps={{
                          classes: {
                            input: classes.otherReason,
                          },
                        }}
                      />
                    </Grid>
                  )}
                </Fragment>
              )}
            </Grid>
          )
        }
        actionsClassName={classes.actions}
        actions={
          <Grid container alignItems="center">
            <Button
              variant="contained"
              onClick={hasParents ? removeParents : submit}
              classes={{
                root: classes.button,
                contained: clsx({ [classes.rejected]: !hasParents }),
              }}
            >
              {hasParents ? "Remove From Content Groups" : "Reject Item"}
            </Button>
            <Button onClick={handleCloseClick}>Cancel</Button>
          </Grid>
        }
      />
    </Fragment>
  );
};

export default RejectedDialog;
